import {
	flip,
	offset,
	shift,
	useClientPoint,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import clsx from "clsx";
import React from "react";
import { useDisclosure } from "../../hooks";
import { Portal } from "../../portal";

/** Props Interface */
export interface HorizontalBarProps {
	name: string;
	value: number;
	formatter?: (item: number) => number | string;
	index: number;
}

export function HorizontalBar({ value, name, formatter, index }: HorizontalBarProps) {
	/** Hooks */
	const popoverDisclosure = useDisclosure();

	/** Memoized */
	const { refs, floatingStyles, context } = useFloating({
		open: popoverDisclosure.isOpen,
		onOpenChange: popoverDisclosure.setIsOpen,
		middleware: [
			offset(20),
			flip({ padding: 20, mainAxis: true }),
			shift({ padding: 20, mainAxis: true }),
		],
	});
	const hover = useHover(context);
	const clientPoint = useClientPoint(context);

	const { getFloatingProps, getReferenceProps } = useInteractions([hover, clientPoint]);

	/** Render */
	return (
		<React.Fragment>
			<div
				className={clsx(
					"bg-linear-40/oklch",
					"flex-center",
					"min-w-max",
					"h-1.5",
					"px-2",
					"cursor-pointer hover:opacity-80",
					"rounded-md",
					"bg-accent-500",
				)}
				style={{ flex: value, filter: `hue-rotate(${index * 30}deg)` }}
				ref={refs.setReference}
				{...getReferenceProps()}
			/>
			<Portal>
				{popoverDisclosure.isOpen && (
					<div
						className={clsx("pointer-events-none", "data-grid min-w-[200px] text-xs card")}
						style={floatingStyles}
						ref={refs.setFloating}
						{...getFloatingProps()}
					>
						<p data-label>{name}</p>
						<p className="font-mono text-right">{formatter ? formatter(value as number) : value}</p>
					</div>
				)}
			</Portal>
		</React.Fragment>
	);
}
